import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "../services/axiosWrapper";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "../services/constants";
import { showToastNotification } from "../reducers/toastSlice";
import { t, untranslated } from "@i18n";
import type {
  AddProjectRoleRequest,
  AddProjectRoleResponse,
  DeleteProjectRoleRequest,
  DeleteProjectRoleResponse,
  ChangeProjectRolePermissionRequest,
  ChangeProjectRolePermissionResponse,
  GetProjectRolesRequest,
  GetProjectRolesResponse,
  ChangeUserRoleFromProjectRequest,
  ChangeUserRoleFromProjectResponse,
  RemoveUsersFromSingleProjectRequest,
  RemoveUsersFromSingleProjectResponse,
  GetProjectObjectResponse,
  TeamsControllerInsertRequest,
  TeamsControllerUpdateRequest,
  TeamsControllerDeleteRequest,
  TeamsControllerGetRequest,
  TeamsControllerResponse,
  LocationControllerInsertRequest,
  LocationControllerUpdateRequest,
  LocationControllerDeleteRequest,
  LocationControllerReorderRequest,
  LocationControllerGetRequest,
  LocationControllerResponse,
  WbsControllerInsertRequest,
  WbsControllerUpdateRequest,
  WbsControllerDeleteRequest,
  WbsControllerGetRequest,
  WbsControllerResponse,
} from "@ServerTypes/projects/project.schema";
import type {
  SaveDefaultSpaceRequest,
  SaveDefaultSpaceResponse,
  SpacesDataRequest,
  SpacesDataResponse,
} from "@ServerTypes/spaces/space.schema";
import type {
  ProjectTemplateConfigHandlerRequest,
  ProjectTemplateConfigHandlerResponse,
} from "@ServerTypes/forms/forms.schema";
import type {
  EditVisibleDataRequest,
  EditVisibleDataResponse,
  SaveStatusDataRequest,
  SaveStatusDataResponse,
  GetStatusDataRequest,
  GetStatusDataResponse,
  GetCategoryDataResponse,
} from "@ServerTypes/status/status.schema";
import {
  setProjectRolesList,
  setCategoryListAction as setCategoryList,
  setStatusData,
  deletedSpaceStatus,
  getProjectSettingObjectAction as getProjectSettingObject,
} from "../reducers/projectSetting";
import type { Exact } from "@Helpers/types/exact";

export async function addProjectRole<T extends Exact<AddProjectRoleRequest, T>>(
  data: T,
): Promise<AddProjectRoleResponse> {
  const response = await axiosWrapper.post<AddProjectRoleResponse>(`${getBackendUrl()}/projects/addProjectRole`, data);
  return response.data;
}

export async function deleteProjectRole<T extends Exact<DeleteProjectRoleRequest, T>>(
  data: T,
): Promise<DeleteProjectRoleResponse> {
  const response = await axiosWrapper.post<DeleteProjectRoleResponse>(
    `${getBackendUrl()}/projects/deleteProjectRole`,
    data,
  );
  return response.data;
}

// updateProjectRolePermission
export async function updateProjectRolePermission<T extends Exact<ChangeProjectRolePermissionRequest, T>>(data: T) {
  const res = await axiosWrapper.post<ChangeProjectRolePermissionResponse>(
    `${getBackendUrl()}/projects/changeProjectRolePermission`,
    data,
  );
  return res;
}

export function getProjectRoles(data: { projectId?: string }) {
  return (dispatch: AppDispatch) => {
    let projectId = data?.projectId;
    if (!projectId) {
      const pathArray = location.pathname.split("/");
      projectId = pathArray[pathArray.length - 2];
    }
    const requestData: GetProjectRolesRequest = { id: projectId };
    axiosWrapper
      .post<GetProjectRolesResponse>(`${getBackendUrl()}/projects/getProjectRoles`, requestData)
      .then((response) => {
        if (response.data.status) {
          dispatch(setProjectRolesList(response.data.data));
        }
      });
  };
}

export function saveProjectRole<T extends Exact<ChangeUserRoleFromProjectRequest, T>>(
  data: T,
  toastdata: { selectedUserfromRow: string; roleChangedTo: string },
  userDataGrid?: any,
) {
  return (dispatch: AppDispatch) => {
    axiosWrapper
      .post<ChangeUserRoleFromProjectResponse>(`${getBackendUrl()}/projects/changeUserRoleFromProject/`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(
            showToastNotification({
              content: t("{{user}}'s role has been changed to {{role}}", {
                user: toastdata.selectedUserfromRow,
                role: toastdata.roleChangedTo,
              }),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
          userDataGrid?.current?.refresh();
        } else {
          dispatch(
            showToastNotification({
              content: untranslated(response.data.errorMessage ?? "Something went wrong"),
              type: TOAST_TYPE_ERROR,
            }),
          );
        }
      });
  };
}
// updateStatusSpaces
export function updateStatusSpaces<T extends Exact<EditVisibleDataRequest, T>>(data: T) {
  return () => {
    axiosWrapper.post<EditVisibleDataResponse>(`${getBackendUrl()}/status/editVisibleData`, data);
  };
}
// addStatusToProject
export function addStatusToProject<T extends Exact<SaveStatusDataRequest, T>>(data: T) {
  return () => {
    axiosWrapper.post<SaveStatusDataResponse>(`${getBackendUrl()}/status/saveStatusData`, data);
  };
}
// getStatusData
export function getStatusData<T extends Exact<GetStatusDataRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    axiosWrapper.post<GetStatusDataResponse>(`${getBackendUrl()}/status/getStatusData`, data).then((response) => {
      if (response.data.status) {
        dispatch(setStatusData(response.data.data));
      }
    });
  };
}
// getAllCategories
export function getAllCategories() {
  return (dispatch: AppDispatch) => {
    axiosWrapper.get<GetCategoryDataResponse>(`${getBackendUrl()}/status/getCategoryData`).then((response) => {
      if (response.data.status) {
        dispatch(setCategoryList(response.data.data));
      }
    });
  };
}
// get list of user which are added into project

// export function deleteSpace(data) {
//   return async (dispatch: AppDispatch) => {
//     const response = await axiosWrapper.post(
//       `${getBackendUrl()}/spaces/deleteSpacesData`,
//       data
//     );

//     dispatch(deletedSpaceStatus(response.data.data));
//     return response.data.data.status;
//   };
// }

// delete space from project
export function deleteSpace(data: { spaceId: string }) {
  return (dispatch: AppDispatch) => {
    const requestData: SpacesDataRequest = {
      action: "remove",
      key: { spaceId: data.spaceId },
    };
    axiosWrapper.post<SpacesDataResponse>(`${getBackendUrl()}/spaces/spacesData`, requestData).then((response) => {
      dispatch(deletedSpaceStatus(response.data.result));
    });
  };
}

// Delete User from a project

export async function removeUsersFromSingleProject<T extends Exact<RemoveUsersFromSingleProjectRequest, T>>(
  data: T,
): Promise<RemoveUsersFromSingleProjectResponse> {
  const response = await axiosWrapper.post<RemoveUsersFromSingleProjectResponse>(
    `${getBackendUrl()}/projects/removeUsersFromSingleProject/`,
    data,
  );
  return response.data;
}

export function getProjectObject(data: { id?: string }) {
  if (data.id) {
    return (dispatch: AppDispatch) => {
      axiosWrapper
        .get<GetProjectObjectResponse>(`${getBackendUrl()}/projects/getProjectObject/${data.id}`)
        .then((res) => {
          if (res.data.status) {
            dispatch(getProjectSettingObject(res.data.data));
          }
        })
        .catch((err: unknown) => {
          console.log("Error in project setting object ", err);
          Promise.reject(err);
        });
    };
  }
}
export type TeamsControllerRequest =
  | TeamsControllerInsertRequest
  | TeamsControllerUpdateRequest
  | TeamsControllerDeleteRequest
  | TeamsControllerGetRequest;

export const teamsController = async <T extends Exact<TeamsControllerRequest, T>>(
  data: T,
): Promise<{ data: TeamsControllerResponse }> => {
  const response = await axiosWrapper.post<TeamsControllerResponse>(
    `${getBackendUrl()}/projects/teamsController`,
    data,
  );
  return response;
};

export type LocationControllerRequest =
  | LocationControllerInsertRequest
  | LocationControllerUpdateRequest
  | LocationControllerDeleteRequest
  | LocationControllerReorderRequest
  | LocationControllerGetRequest;

export const locationController = async <T extends Exact<LocationControllerRequest, T>>(
  data: T,
): Promise<{ data: LocationControllerResponse }> => {
  const response = await axiosWrapper.post<LocationControllerResponse>(
    `${getBackendUrl()}/projects/locationController`,
    data,
  );
  return response;
};

export const projectTemplateConfigHandler = async <T extends Exact<ProjectTemplateConfigHandlerRequest, T>>(
  data: T,
): Promise<{ data: ProjectTemplateConfigHandlerResponse }> => {
  const response = await axiosWrapper.post<ProjectTemplateConfigHandlerResponse>(
    `${getBackendUrl()}/forms/projectTemplateConfigHandler`,
    data,
  );
  return response;
};

export type WBSControllerRequest =
  | WbsControllerInsertRequest
  | WbsControllerUpdateRequest
  | WbsControllerDeleteRequest
  | WbsControllerGetRequest;

export const WBSController = async <T extends Exact<WBSControllerRequest, T>>(
  data: T,
): Promise<{ data: WbsControllerResponse }> => {
  const response = await axiosWrapper.post<WbsControllerResponse>(`${getBackendUrl()}/projects/WBSController`, data);
  return response;
};
export const saveDefaultSpace = async <T extends Exact<SaveDefaultSpaceRequest, T>>(
  data: T,
): Promise<{ data: SaveDefaultSpaceResponse }> => {
  return await axiosWrapper.post<SaveDefaultSpaceResponse>(`${getBackendUrl()}/spaces/saveDefaultSpace`, data);
};
